import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { FiAlertCircle, FiCalendar, FiCheckCircle, FiDownload, FiEdit3, FiFileText, FiGlobe, FiInfo, FiMoon, FiShield, FiSliders, FiSun, FiUser } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { onUsersChange, updateUserPassword, updateUserProfile } from '../services/userService';
import useIsMobile from '../hooks/useIsMobile';
import { getUserKey, readScopedStorage } from '../utils/userStorage';
import { getAllowedBoards } from '../utils/roleAccess';
import { buildReportRows, getDeadlineClosureSummary, groupReportRowsByBoard } from '../utils/reportUtils';

const load = (key, def) => {
  try { const s = localStorage.getItem(key); return s !== null ? JSON.parse(s) : def; }
  catch { return def; }
};

const EyeIcon = ({ visible }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    {visible
      ? (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>)
      : (<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>)}
  </svg>
);

const getCalendarGrid = (monthDate) => {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

const formatReportRange = (start, end) => {
  const format = (value) => {
    if (!value) return 'Tanlanmagan';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? 'Noto‘g‘ri sana' : date.toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  return `${format(start)} — ${format(end)}`;
};

const Profile = () => {
  const { theme, toggleTheme } = useTheme();
  const { t, language: currentLang, setLanguage } = useI18n();
  const { currentUser, currentRole, updateCurrentUser } = useAuth();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();
  const fileRef = useRef(null);
  const isAdmin = currentRole === 'Admin' || !currentUser;

  const [tab, setTab]      = useState(0);
  const [prevTab, setPrev] = useState(0);

  const [name,     setName]     = useState(() => currentUser ? (currentUser.name || '') : load(getUserKey('name'), 'Admin'));
  const [email,    setEmail]    = useState(() => currentUser ? (currentUser.email || '') : load(getUserKey('email'), ''));
  const [status,   setStatus]   = useState(() => currentUser ? (currentUser.role || currentRole) : load(getUserKey('status'), 'Admin'));
  const [avatar,   setAvatar]   = useState(() => load(getUserKey('avatar'),   ''));
  const [accent,   setAccent]   = useState(() => load(getUserKey('accent'),   'blue'));
  const [localLang, setLocalLang] = useState(() => load(getUserKey('language'), "O'zbekcha"));
  const [storedPwd, setStoredPwd] = useState(() => currentUser ? (currentUser.password || '') : load(getUserKey('password'), process.env.REACT_APP_ADMIN_PASSWORD || ''));

  const [curPwd,  setCurPwd]  = useState('');
  const [newPwd,  setNewPwd]  = useState('');
  const [confPwd, setConfPwd] = useState('');
  const [showCur,  setShowCur]  = useState(false);
  const [showNew,  setShowNew]  = useState(false);
  const [showConf, setShowConf] = useState(false);

  const [toast,        setToast]        = useState(null);
  const [showLangModal, setShowLangModal] = useState(false);
  const [showReportCalendar, setShowReportCalendar] = useState(false);
  const [reportBoard, setReportBoard] = useState('all');
  const [reportStartDate, setReportStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportEndDate, setReportEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportCalendarMonth, setReportCalendarMonth] = useState(() => new Date());
  const [reportDraftStartDate, setReportDraftStartDate] = useState('');
  const [reportDraftEndDate, setReportDraftEndDate] = useState('');
  const [allMembers, setAllMembers] = useState([]);

  const kanbanBoards = readScopedStorage('kanban', { dizayn: {}, montaj: {}, 'vfx-ai': {} });
  const allowedBoardIds = getAllowedBoards(currentRole);
  const boardOptions = [
    { id: 'all', label: 'Hammasi' },
    ...Object.keys(kanbanBoards)
      .filter((boardId) => boardId !== '_order' && (!allowedBoardIds || allowedBoardIds.includes(boardId)))
      .map((boardId) => ({
        id: boardId,
        label: boardId.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
      }))
  ];

  const ACCENT_OPTIONS = [
    { id: 'blue',   color: '#3b82f6', label: t('settings.colors.blue')   },
    { id: 'purple', color: '#8b5cf6', label: t('settings.colors.purple') },
    { id: 'green',  color: '#22c55e', label: t('settings.colors.green')  },
    { id: 'orange', color: '#f97316', label: t('settings.colors.orange') },
  ];

  const buttonStyle = { border: 'none', borderRadius: '10px', background: 'transparent', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' };

  const TABS = [
    { id: 0, label: t('settings.langTab'),     icon: FiGlobe },
    { id: 1, label: t('settings.profileTab'),  icon: FiUser },
    { id: 2, label: t('settings.designTab'),   icon: FiSliders },
    { id: 3, label: t('settings.securityTab'), icon: FiShield },
    { id: 5, label: 'Hisobot', icon: FiFileText },
  ];

  const ABOUT_TAB = { id: 4, label: 'Sayt haqida', icon: FiInfo };
  const AboutTabIcon = ABOUT_TAB.icon;

  const accentColor = ACCENT_OPTIONS.find(a => a.id === accent)?.color || '#3b82f6';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    localStorage.setItem(getUserKey('name'),   JSON.stringify(name));
    localStorage.setItem(getUserKey('email'),  JSON.stringify(email));
    localStorage.setItem(getUserKey('status'), JSON.stringify(status));
    localStorage.setItem(getUserKey('avatar'), JSON.stringify(avatar));
    window.dispatchEvent(new Event('profileUpdated'));
  }, [name, email, status, avatar]);

  useEffect(() => {
    localStorage.setItem(getUserKey('accent'), JSON.stringify(accent));
    try { document.documentElement.style.setProperty('--accent-color', accentColor); } catch {}
    window.dispatchEvent(new Event('profileUpdated'));
  }, [accent, accentColor]);

  useEffect(() => { localStorage.setItem(getUserKey('language'), JSON.stringify(localLang)); }, [localLang]);
  useEffect(() => { localStorage.setItem(getUserKey('password'), JSON.stringify(storedPwd)); }, [storedPwd]);

  useEffect(() => {
    const unsubscribe = onUsersChange((users, error) => {
      if (error || !Array.isArray(users)) return;
      setAllMembers(users.map((user) => ({ id: user.id, name: user.name || user.email || 'Noma’lum', email: user.email || '', role: user.role || '' })));
    });
    return () => unsubscribe();
  }, []);

  const reportRows = buildReportRows(
    kanbanBoards,
    reportBoard,
    null,
    Object.fromEntries(allMembers.map((member) => [member.id, member])),
    reportStartDate,
    reportEndDate
  );
  const groupedReportRows = groupReportRowsByBoard(reportRows);
  const reportSummary = (() => {
    const total = reportRows.length;
    return { total, ...getDeadlineClosureSummary(reportRows) };
  })();

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAvatar(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { showToast(t('settings.toastFieldsRequired'), 'error'); return; }
    if (!isAdmin && currentUser?.id) {
      try {
        await updateUserProfile(currentUser.id, { name: name.trim(), email: email.trim().toLowerCase() });
        updateCurrentUser({ name: name.trim(), email: email.trim().toLowerCase() });
      } catch {
        showToast("Saqlashda xato yuz berdi", 'error'); return;
      }
    }
    showToast(t('settings.toastProfileSaved'));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!curPwd || !newPwd || !confPwd) { showToast(t('settings.toastPwdRequired'), 'error'); return; }
    if (curPwd !== storedPwd)           { showToast(t('settings.toastPwdError'), 'error'); return; }
    if (newPwd !== confPwd)             { showToast(t('settings.toastPwdMismatch'), 'error'); return; }
    if (newPwd.length < 6)              { showToast(t('settings.toastPwdShort'), 'error'); return; }
    if (!isAdmin && currentUser?.id) {
      try { await updateUserPassword(currentUser.id, newPwd); } catch {}
    }
    setStoredPwd(newPwd);
    setCurPwd(''); setNewPwd(''); setConfPwd('');
    showToast(t('settings.toastPwdUpdated'));
  };

  const handleReportDayClick = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;

    if (!reportDraftStartDate || reportDraftEndDate) {
      setReportDraftStartDate(isoDate);
      setReportDraftEndDate('');
      setReportCalendarMonth(new Date(year, date.getMonth(), 1));
      return;
    }

    if (isoDate < reportDraftStartDate) {
      setReportDraftEndDate(reportDraftStartDate);
      setReportDraftStartDate(isoDate);
    } else {
      setReportDraftEndDate(isoDate);
    }
  };

  const confirmReportDateRange = () => {
    if (!reportDraftStartDate || !reportDraftEndDate) {
      showToast('Boshlanish va tugash sanasini tanlang.', 'error');
      return;
    }
    setReportStartDate(reportDraftStartDate);
    setReportEndDate(reportDraftEndDate);
    setShowReportCalendar(false);
  };

  const cancelReportDateRange = () => {
    setReportDraftStartDate(reportStartDate);
    setReportDraftEndDate(reportEndDate);
    setShowReportCalendar(false);
  };

  const openReportCalendar = () => {
    setReportDraftStartDate(reportStartDate);
    setReportDraftEndDate(reportEndDate);
    setReportCalendarMonth(new Date(`${reportStartDate || reportEndDate}T00:00:00`));
    setShowReportCalendar(true);
  };

  const changeTab = (id) => { setPrev(tab); setTab(id); };
  const dir = tab >= prevTab ? 1 : -1;

  const downloadBoardReportPdf = () => {
    if (!reportStartDate || !reportEndDate) {
      showToast('Boshlanish va tugash sanasini tanlang.', 'error');
      setShowReportCalendar(true);
      return;
    }
    if (new Date(`${reportStartDate}T00:00:00`) > new Date(`${reportEndDate}T00:00:00`)) {
      showToast('Tugash sanasi boshlanish sanasidan oldin bo‘lishi mumkin emas.', 'error');
      return;
    }
    if (!reportRows.length) {
      showToast('Tanlangan sana oralig‘ida kartochka mavjud emas.', 'error');
      return;
    }

    const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    const contentWidth = pageWidth - margin * 2;
    const today = new Date().toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const boardLabels = Object.keys(groupedReportRows);
    let y = 56;
    const visibleRows = reportRows;

    const addHeader = (pageNumber) => {
      doc.setFillColor(15, 23, 42);
      doc.roundedRect(margin, 24, contentWidth, 80, 18, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(23);
      doc.text('SMM CRM — Hisobot', margin + 22, 57);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`${formatReportRange(reportStartDate, reportEndDate)} • ${visibleRows.length} ta kartochka`, margin + 22, 76);
      doc.text(`Yaratildi: ${today}`, margin + 22, 91);
      doc.setFillColor(59, 130, 246);
      doc.roundedRect(pageWidth - margin - 132, 39, 100, 30, 15, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`Sahifa ${pageNumber}`, pageWidth - margin - 66, 58, { align: 'center' });
    };

    const addFooter = (pageNumber) => {
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.8);
      doc.line(margin, pageHeight - 36, pageWidth - margin, pageHeight - 36);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('SMM CRM • Bitta umumiy hisobot', margin, pageHeight - 22);
      doc.text(`${pageNumber}`, pageWidth - margin, pageHeight - 22, { align: 'right' });
    };

    const addSummaryBoxes = () => {
      const total = reportRows.length;
      const closures = getDeadlineClosureSummary(reportRows);

      const stats = [
        ['Jami', total, '#2563eb'],
        ['Deadlinegacha yopilgan', closures.onTime, '#22c55e'],
        ["Deadline'dan keyin yopilgan", closures.late, '#ef4444'],
      ];

      const boxWidth = (contentWidth - 16) / stats.length;
      stats.forEach(([label, value, color], index) => {
        const x = margin + index * (boxWidth + 4);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, y, boxWidth, 56, 12, 12, 'F');
        doc.setDrawColor(148, 163, 184);
        doc.setLineWidth(0.7);
        doc.roundedRect(x, y, boxWidth, 56, 12, 12, 'S');
        doc.setTextColor(71, 85, 105);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(String(label), x + 12, y + 18);
        doc.setTextColor(color);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text(String(value), x + 12, y + 40);
      });
      y += 80;
    };

    const addBoardSummary = () => {
      const boardNames = Object.keys(groupedReportRows);
      if (!boardNames.length) return;

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.text('Doskalar bo‘yicha umumiy ko‘rsatkich', margin, y);
      y += 22;

      boardNames.forEach((boardName) => {
        const rows = groupedReportRows[boardName] || [];
        const closures = getDeadlineClosureSummary(rows);
        const label = `${boardName}: ${rows.length} ta (deadlinegacha ${closures.onTime}, deadline'dan keyin ${closures.late})`;
        doc.setTextColor(71, 85, 105);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(label, margin, y);
        y += 16;
      });

      y += 10;
    };

    addHeader(1);
    y = 122;
    const summaryText = `Doskalar: ${boardLabels.join(', ') || 'Tanlanmagan'} / Sana oralig‘i: ${formatReportRange(reportStartDate, reportEndDate)}`;
    const summaryLines = doc.splitTextToSize(summaryText, contentWidth);
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(summaryLines, margin, y, { maxWidth: contentWidth });
    y += summaryLines.length * 12 + 16;

    if (!visibleRows.length) {
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text('Bu sana oralig‘ida kartochka mavjud emas.', margin, y);
      addFooter(1);
    } else {
      addSummaryBoxes();
      addBoardSummary();
      addFooter(doc.internal.getNumberOfPages());
    }

    doc.save(`SMM_Hisobot_${reportStartDate}_${reportEndDate}.pdf`);
  };

  const downloadSiteInfoPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const accent = [59, 130, 246];
    const dark = [15, 23, 42];
    const muted = [148, 163, 184];
    const paper = [248, 250, 252];
    const contentWidth = pageWidth - 96;
    const left = 48;
    let y = 0;

    const sections = [
      {
        title: 'Platforma haqida',
        paragraphs: [
          'Bu platforma kim tomonidan yaratilgan?',
          'Ushbu platforma Shaxram Razzoqov tomonidan yaratilgan.',
          'Shaxram Razzoqov — SMM sohasida faoliyat yurituvchi mutaxassis va ushbu platformaning g‘oyasi, konsepsiyasi hamda rivojlanish yo‘nalishini ishlab chiqqan loyiha muallifi.',
          'Platforma real ish jarayonlarida yuzaga keladigan muammolarni hal qilish va SMM hamda kreativ jamoalar faoliyatini maksimal darajada tizimlashtirish maqsadida ishlab chiqilgan.'
        ]
      },
      {
        title: 'G‘oya qanday paydo bo‘lgan?',
        paragraphs: [
          'Platforma g‘oyasi 2025-yilda paydo bo‘lgan.',
          'SMM jarayonlarida vazifalarning turli chatlarda yo‘qolib ketishi, dizayner va montajyorlar bilan ishlashdagi kommunikatsiya muammolari, deadline\'larni nazorat qilishning qiyinligi va umumiy ish jarayonining yetarlicha tizimlashmaganligi asosiy muammolardan biri edi.',
          'Bundan tashqari, jamoa ichidagi kommunikatsiya, vazifalarga bo‘lgan mas’uliyat, ish sifati va topshiriqlarning aniqligi bilan bog‘liq muammolar ham platformani yaratish zaruratini yuzaga keltirdi.',
          'Shu ehtiyojdan kelib chiqib, barcha jarayonlarni bitta markazlashgan tizimga birlashtirish g‘oyasi shakllandi.'
        ]
      },
      {
        title: 'Platforma qanday yaratilgan?',
        paragraphs: [
          'Loyiha ustida ishlash 2025-yilda boshlangan g‘oyadan keyin bosqichma-bosqich davom ettirildi.',
          'Platformaning birinchi ishlaydigan versiyasi 2026-yil 22-iyunda tayyorlandi.',
          'Ishlab chiqish jarayonida zamonaviy dasturlash vositalari va texnologiyalaridan, jumladan Visual Studio Code va EmailJS xizmatidan foydalanildi.',
          'Loyihaning ishlab chiqilishida sun’iy intellekt texnologiyalari ham yordamchi vosita sifatida faol qo‘llanildi.',
          'Xususan, ChatGPT, Claude, Gemini va GitHub kabi vositalardan kod yozish, texnik yechimlarni ishlab chiqish, xatolarni aniqlash va tuzatish, shuningdek interfeys va dizayn yechimlarini takomillashtirishda foydalanildi.'
        ]
      },
      {
        title: 'Platformaning asosiy maqsadi',
        paragraphs: [
          'Platformaning asosiy maqsadi — SMM va kreativ jamoalarning ishini maksimal darajada tizimlashtirish, tartibga solish va boshqarishni soddalashtirish.',
          'Bu shunchaki vazifalar ro‘yxati emas. Platforma orqali ish jarayonini boshidan oxirigacha nazorat qilish, jamoa a’zolari o‘rtasidagi hamkorlikni yaxshilash va har bir vazifaning holatini aniq ko‘rish imkoniyati yaratiladi.'
        ]
      },
      {
        title: 'Kimlar uchun yaratilgan?',
        list: ['SMM menejerlar', 'SMM va marketing agentliklari', 'Freelancerlar', 'Dizaynerlar', 'Video montajyorlar', 'Kompaniyalar va ichki kreativ jamoalar']
      },
      {
        title: 'Platformaning imkoniyatlari',
        paragraphs: [
          'Platformadagi asosiy funksiyalar real vaqt rejimida ishlaydi va ish jarayonlarini yagona tizim orqali boshqarishga imkon beradi.',
          'Vazifalar, loyihalar, jamoa faoliyati, muddatlar va boshqa ish jarayonlarini bir joyda nazorat qilish mumkin.'
        ]
      },
      {
        title: 'Platformani boshqalardan ajratib turadigan jihat',
        paragraphs: [
          'Ushbu platformaning asosiy g‘oyasi — foydalanuvchiga erkinlik va nazoratni bir vaqtning o‘zida berish.',
          'Ish tizimlashtirilgani sari vazifalar tartibga tushadi, vaqt samaraliroq taqsimlanadi va inson faqat ish jarayonini kuzatish bilan cheklanib qolmay, o‘z vaqtini boshqa muhim ishlar va shaxsiy hayotiga ham ajrata oladi.',
          'Maqsad — ko‘proq ishlash emas, balki ishni to‘g‘ri tashkil qilish.'
        ]
      },
      {
        title: 'Kelajakdagi rivojlanish',
        paragraphs: [
          'Platforma yakunlangan mahsulot sifatida emas, balki doimiy rivojlanib boradigan tizim sifatida ko‘riladi.',
          'Kelajakda platformaga Telegram bot, avtomatik eslatmalar, AI yordamchi, kengaytirilgan analitika, mijozlar uchun alohida kabinet va avtomatik hisobotlar kabi imkoniyatlarni qo‘shish rejalashtirilgan.'
        ]
      },
      {
        title: 'Asosiy g‘oya',
        paragraphs: [
          'Ishni o‘zingizga moslashtiring. Jarayonlarni tizimlashtiring. Vaqtingizni qadrlang.',
          'Ushbu platforma bir muammodan kelib chiqqan oddiy g‘oya sifatida boshlangan va real ish jarayonlari asosida shakllangan tizimga aylangan.',
          'Uning asosiy maqsadi — insonni ishga bog‘lab qo‘yish emas, ish jarayonini shunday tashkil qilishki, inson o‘z ishini nazorat qilgan holda o‘z vaqtini ham boshqara olsin.'
        ]
      }
    ];

    const drawBackground = () => {
      doc.setFillColor(paper[0], paper[1], paper[2]);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      doc.setFillColor(dark[0], dark[1], dark[2]);
      doc.rect(0, 0, pageWidth, 10, 'F');
    };

    const drawFooter = () => {
      const page = doc.internal.getNumberOfPages();
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.7);
      doc.line(left, pageHeight - 38, pageWidth - left, pageHeight - 38);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(muted[0], muted[1], muted[2]);
      doc.text('SMM-CRM.UZ  |  Sayt haqida', left, pageHeight - 22);
      doc.text(`Sahifa ${page}`, pageWidth - left, pageHeight - 22, { align: 'right' });
    };

    const newPage = () => {
      drawFooter();
      doc.addPage();
      drawBackground();
      y = 58;
    };

    drawBackground();
    doc.setFillColor(dark[0], dark[1], dark[2]);
    doc.roundedRect(left, 58, contentWidth, 220, 24, 24, 'F');
    doc.setFillColor(accent[0], accent[1], accent[2]);
    doc.circle(pageWidth - 102, 112, 42, 'F');
    doc.setFillColor(255, 255, 255);
    doc.circle(pageWidth - 102, 112, 19, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.text('SMM-CRM.UZ', left + 28, 116);
    doc.setFontSize(13);
    doc.setTextColor(191, 219, 254);
    doc.text('Rasmiy loyiha taqdimoti', left + 30, 145);
    doc.setFontSize(32);
    doc.setTextColor(255, 255, 255);
    doc.text('Sayt haqida', left + 28, 214);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(203, 213, 225);
    doc.text('SMM va kreativ jamoalar uchun markazlashgan ish platformasi', left + 30, 242);
    doc.setFillColor(accent[0], accent[1], accent[2]);
    doc.roundedRect(left, 312, 190, 34, 17, 17, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('MUALLIF: SHAXRAM RAZZOQOV', left + 18, 334);
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text('Ushbu hujjatda platformaning g‘oyasi, yaratilish tarixi,', left, 402);
    doc.text('maqsadi, imkoniyatlari va kelajakdagi rivojlanish yo‘nalishlari jamlangan.', left, 423);
    doc.setFontSize(10);
    doc.setTextColor(muted[0], muted[1], muted[2]);
    doc.text('smm-crm.uz  •  2026', left, pageHeight - 60);
    drawFooter();
    doc.addPage();
    drawBackground();
    y = 58;

    sections.forEach((section, index) => {
      const titleLines = doc.splitTextToSize(section.title, contentWidth - 52);
      const bodyItems = section.paragraphs || section.list || [];
      const itemLines = bodyItems.reduce((total, item) => total + doc.splitTextToSize(item, contentWidth - 70).length, 0);
      const cardHeight = 54 + itemLines * 15 + (section.list ? 12 : bodyItems.length * 5);
      if (y + Math.min(cardHeight, 260) > pageHeight - 68) newPage();

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.8);
      doc.roundedRect(left, y, contentWidth, cardHeight, 14, 14, 'FD');
      doc.setFillColor(accent[0], accent[1], accent[2]);
      doc.roundedRect(left, y, 7, cardHeight, 4, 4, 'F');
      doc.setTextColor(dark[0], dark[1], dark[2]);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.text(titleLines, left + 24, y + 28, { lineHeightFactor: 1.1 });
      let textY = y + 53 + (titleLines.length - 1) * 16;
      doc.setFontSize(10.5);
      bodyItems.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 70);
        if (section.list) {
          doc.setFillColor(accent[0], accent[1], accent[2]);
          doc.circle(left + 29, textY - 3, 2.5, 'F');
          doc.setTextColor(51, 65, 85);
          doc.setFont('helvetica', 'normal');
          doc.text(lines, left + 40, textY, { lineHeightFactor: 1.35 });
        } else {
          doc.setTextColor(51, 65, 85);
          doc.setFont('helvetica', 'normal');
          doc.text(lines, left + 24, textY, { lineHeightFactor: 1.35 });
        }
        textY += lines.length * 14 + (section.list ? 5 : 8);
      });
      y += cardHeight + 18;
      if (index === sections.length - 1 || y > pageHeight - 150) {
        if (index !== sections.length - 1) newPage();
      }
    });

    if (y + 118 > pageHeight - 68) newPage();
    doc.setFillColor(dark[0], dark[1], dark[2]);
    doc.roundedRect(left, y, contentWidth, 106, 16, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Loyiha muallifi', left + 24, y + 31);
    doc.setFontSize(19);
    doc.text('Shaxram Razzoqov', left + 24, y + 59);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(191, 219, 254);
    doc.text('SMM mutaxassisi, loyiha g‘oyasi va konsepsiyasi muallifi', left + 24, y + 81);
    drawFooter();
    doc.save('smm-crm-uz.pdf');
  };

  const bg   = isDark ? '#0f172a' : '#f1f5f9';
  const card = isDark ? '#1e293b' : '#ffffff';
  const bord = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const tp   = isDark ? '#f8fafc' : '#0f172a';
  const ts   = isDark ? '#94a3b8' : '#64748b';
  const ibg  = isDark ? '#0f172a' : '#f8fafc';
  const ibrd = isDark ? '#334155' : '#e2e8f0';

  const inp = {
    width: '100%', padding: '13px 16px', borderRadius: '14px',
    border: `1.5px solid ${ibrd}`, background: ibg, color: tp,
    fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const pwdStrength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const strength = pwdStrength(newPwd);
  const sColor = ['#ef4444','#f97316','#eab308','#22c55e','#16a34a'][strength - 1] || ibrd;
  const pwdStrengthLabels = t('settings.pwdStrength');
  const sLabel = Array.isArray(pwdStrengthLabels) ? pwdStrengthLabels[strength - 1] || '' : '';

  const tabSlide = { type: 'spring', stiffness: 280, damping: 24 };

  return (
    <div style={{ minHeight: '100vh', background: bg, padding: isMobile ? '14px' : '24px', color: tp, fontFamily: 'Inter, sans-serif', transition: 'background 0.3s', boxSizing: 'border-box' }}>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key="toast"
            initial={{ opacity: 0, y: -24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -24, scale: 0.95 }}
            style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 99999,
              padding: '14px 24px', borderRadius: '16px',
              background: toast.type === 'error' ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
              color: '#fff', fontWeight: 700, fontSize: '14px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
              display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
            {toast.type === 'error' ? <FiAlertCircle size={17} /> : <FiCheckCircle size={17} />} {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Language confirm modal */}
      <AnimatePresence>
        {showLangModal && (
          <motion.div key="lm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowLangModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ width: '440px', maxWidth: '90vw', borderRadius: '24px', padding: '32px', background: card, border: `1px solid ${bord}`, boxShadow: '0 30px 80px rgba(0,0,0,0.4)', color: tp }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}><FiGlobe size={23} strokeWidth={1.8} /></div>
              <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: 700 }}>{t('settings.langModalTitle')}</h3>
              <p style={{ margin: '0 0 28px', color: ts, lineHeight: 1.6 }}>
                {t('settings.langModalBody').replace('{lang}', localLang)}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowLangModal(false)}
                  style={{ flex: 1, padding: '13px', borderRadius: '14px', border: `1.5px solid ${ibrd}`, background: 'transparent', color: tp, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px' }}>
                  {t('settings.cancelBtn')}
                </button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button"
                  onClick={() => {
                    localStorage.setItem(getUserKey('language'), JSON.stringify(localLang));
                    try { setLanguage(localLang); } catch {}
                    window.location.reload();
                  }}
                  style={{ flex: 1, padding: '13px', borderRadius: '14px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px' }}>
                  {t('settings.confirmBtn')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '24px', alignItems: 'flex-start' }}>

        {/* ── Left sidebar ── */}
        <div style={{ width: isMobile ? '100%' : '270px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Profile card */}
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
            style={{ borderRadius: '24px', background: card, border: `1px solid ${bord}`, padding: '28px 22px', textAlign: 'center', boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)' }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: '16px' }}>
              <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }} onClick={() => fileRef.current?.click()}
                style={{ width: '88px', height: '88px', borderRadius: '26px', background: avatar ? 'transparent' : `linear-gradient(135deg,${accentColor},${accentColor}99)`, border: `3px solid ${accentColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 auto', boxShadow: `0 8px 28px ${accentColor}44`, overflow: 'hidden' }}>
                {avatar
                  ? <img src={avatar} alt="av" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ color: '#fff', fontWeight: 800, fontSize: '34px' }}>{name.charAt(0).toUpperCase()}</span>}
              </motion.div>
              <div onClick={() => fileRef.current?.click()}
                style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '28px', height: '28px', borderRadius: '50%', background: accentColor, border: `2.5px solid ${card}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px' }}>
                <FiEdit3 size={13} aria-hidden="true" />
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />

            <h3 style={{ margin: '0 0 5px', fontSize: '17px', fontWeight: 700 }}>{name}</h3>
            <p style={{ margin: '0 0 12px', color: ts, fontSize: '13px', wordBreak: 'break-all', lineHeight: 1.4 }}>{email}</p>
            <span style={{ display: 'inline-block', padding: '5px 14px', borderRadius: '999px', background: accentColor + '20', color: accentColor, fontSize: '12px', fontWeight: 700, border: `1px solid ${accentColor}30` }}>
              {status}
            </span>
          </motion.div>

          {/* Nav tabs */}
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            style={{ borderRadius: '24px', background: card, border: `1px solid ${bord}`, padding: '12px', boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)' }}>
            {TABS.map(item => {
              const TabIcon = item.icon;
              return (
                <motion.button key={item.id} whileHover={{ x: 4 }} whileTap={{ scale: 0.97 }} onClick={() => changeTab(item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '13px 16px', borderRadius: '14px', border: 'none', background: tab === item.id ? accentColor + '18' : 'transparent', color: tab === item.id ? accentColor : ts, fontWeight: tab === item.id ? 700 : 500, fontSize: '14px', cursor: 'pointer', textAlign: 'left', marginBottom: '4px', borderLeft: tab === item.id ? `3px solid ${accentColor}` : '3px solid transparent', transition: 'all 0.2s', fontFamily: 'inherit' }}>
                  <TabIcon size={17} strokeWidth={1.8} aria-hidden="true" />
                  {item.label}
                </motion.button>
              );
            })}

            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${bord}` }}>
              <motion.button whileHover={{ x: 4 }} whileTap={{ scale: 0.97 }} onClick={() => changeTab(ABOUT_TAB.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '13px 16px', borderRadius: '14px', border: 'none', background: tab === ABOUT_TAB.id ? accentColor + '18' : 'transparent', color: tab === ABOUT_TAB.id ? accentColor : ts, fontWeight: tab === ABOUT_TAB.id ? 700 : 500, fontSize: '14px', cursor: 'pointer', textAlign: 'left', borderLeft: tab === ABOUT_TAB.id ? `3px solid ${accentColor}` : '3px solid transparent', transition: 'all 0.2s', fontFamily: 'inherit' }}>
                <AboutTabIcon size={17} strokeWidth={1.8} aria-hidden="true" />
                {ABOUT_TAB.label}
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* ── Main content ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          style={{ flex: 1, borderRadius: '24px', background: card, border: `1px solid ${bord}`, padding: '36px', boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)', minHeight: '520px', overflow: 'hidden' }}>

          <AnimatePresence mode="wait">

            {/* ── Tab 0: Til ── */}
            {tab === 0 && (
              <motion.div key="lang" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiGlobe size={21} strokeWidth={1.8} /></div>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>{t('settings.languageTitle')}</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>{t('settings.languageDescription')}</p>
                  </div>
                  <span style={{ padding: '6px 14px', borderRadius: '999px', background: accentColor, color: '#fff', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>{currentLang}</span>
                </div>
                <form onSubmit={e => { e.preventDefault(); setShowLangModal(true); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {["O'zbekcha", "Русский", "English"].map(lang => (
                    <motion.label key={lang} whileHover={{ scale: 1.01 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', borderRadius: '16px', cursor: 'pointer', border: `1.5px solid ${localLang === lang ? accentColor : ibrd}`, background: localLang === lang ? accentColor + '10' : ibg, transition: 'all 0.2s' }}>
                      <input type="radio" value={lang} checked={localLang === lang} onChange={() => setLocalLang(lang)} style={{ display: 'none' }} />
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: `2px solid ${localLang === lang ? accentColor : ibrd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {localLang === lang && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: accentColor }} />}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: localLang === lang ? accentColor : tp }}>{lang}</span>
                      {localLang === lang && <span style={{ marginLeft: 'auto', color: accentColor, fontWeight: 700 }}>✓</span>}
                    </motion.label>
                  ))}
                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ marginTop: '8px', padding: '14px 28px', borderRadius: '14px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, fontSize: '15px', cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit' }}>
                    {t('settings.saveLanguage')}
                  </motion.button>
                </form>
              </motion.div>
            )}

            {/* ── Tab 1: Profil ── */}
            {tab === 1 && (
              <motion.div key="profile" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiUser size={21} strokeWidth={1.8} /></div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>{t('settings.profileTitle')}</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>{t('settings.profileDescription')}</p>
                  </div>
                </div>
                <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* Avatar upload */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px', borderRadius: '18px', border: `1.5px dashed ${ibrd}`, background: ibg }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: avatar ? 'transparent' : `linear-gradient(135deg,${accentColor},${accentColor}99)`, border: `2px solid ${accentColor}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {avatar ? <img src={avatar} alt="av" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: '24px' }}>{name.charAt(0).toUpperCase()}</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '15px' }}>{t('settings.avatarTitle')}</p>
                      <p style={{ margin: '0 0 12px', color: ts, fontSize: '13px' }}>{t('settings.avatarDesc')}</p>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => fileRef.current?.click()}
                          style={{ padding: '8px 16px', borderRadius: '10px', border: 'none', background: accentColor, color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          {t('settings.avatarUpload')}
                        </motion.button>
                        {avatar && (
                          <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setAvatar('')}
                            style={{ padding: '8px 16px', borderRadius: '10px', border: '1.5px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                            {t('settings.avatarDelete')}
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </div>

                  {[
                    { label: t('settings.labels.fullName'), val: name,   set: setName,   type: 'text',  ph: 'Shaxram Razzoqov', editable: true },
                    { label: t('settings.labels.email'),    val: email,  set: setEmail,  type: 'email', ph: 'email@example.com', editable: true },
                    { label: t('settings.labels.status'),   val: status, set: setStatus, type: 'text',  ph: 'Admin', editable: isAdmin },
                  ].map(({ label, val, set, type, ph, editable }) => (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, color: ts }}>{label}</label>
                      <input type={type} value={val} onChange={e => editable && set(e.target.value)} placeholder={ph}
                        readOnly={!editable}
                        style={{ ...inp, opacity: editable ? 1 : 0.6, cursor: editable ? 'text' : 'default' }}
                        onFocus={e => { if (editable) e.target.style.borderColor = accentColor; }}
                        onBlur={e => { if (editable) e.target.style.borderColor = ibrd; }} />
                    </div>
                  ))}

                  {!isAdmin && (
                    <p style={{ margin: 0, fontSize: '13px', color: ts, padding: '12px 16px', borderRadius: '12px', background: ibg, border: `1px solid ${ibrd}` }}>
                      Rolni faqat Admin o'zgartira oladi.
                    </p>
                  )}

                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ padding: '14px 28px', borderRadius: '14px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, fontSize: '15px', cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit' }}>
                    {t('settings.saveProfile')}
                  </motion.button>
                </form>
              </motion.div>
            )}

            {/* ── Tab 2: Dizayn ── */}
            {tab === 2 && (
              <motion.div key="design" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiSliders size={21} strokeWidth={1.8} /></div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>{t('settings.designTitle')}</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>{t('settings.designDescription')}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                  {/* Theme toggle */}
                  <div>
                    <p style={{ margin: '0 0 14px', fontWeight: 700, fontSize: '15px' }}>{t('settings.themeTitle')}</p>
                    <div style={{ display: 'flex', gap: '14px' }}>
                      {[
                        { id: 'light', label: t('settings.themeLight'), icon: FiSun },
                        { id: 'dark',  label: t('settings.themeDark'),  icon: FiMoon },
                      ].map(item => {
                        const isActive = (item.id === 'dark') === isDark;
                        const ThemeIcon = item.icon;
                        return (
                          <motion.button key={item.id} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} type="button"
                            onClick={() => { if (!isActive) toggleTheme(); showToast(t('settings.toastThemeChanged')); }}
                            style={{ flex: 1, padding: '22px 16px', borderRadius: '18px', cursor: 'pointer', border: `2px solid ${isActive ? accentColor : ibrd}`, background: isActive ? accentColor + '15' : ibg, color: isActive ? accentColor : ts, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'all 0.25s', fontFamily: 'inherit' }}>
                            <ThemeIcon size={27} strokeWidth={1.7} aria-hidden="true" />
                            <span style={{ fontWeight: 700, fontSize: '14px' }}>{item.label}</span>
                            {isActive && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: accentColor + '20', color: accentColor, fontWeight: 700 }}>{t('settings.themeActive')}</span>}
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Accent colors */}
                  <div>
                    <p style={{ margin: '0 0 14px', fontWeight: 700, fontSize: '15px' }}>{t('settings.accentTitle')}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                      {ACCENT_OPTIONS.map(opt => (
                        <motion.button key={opt.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button"
                          onClick={() => { setAccent(opt.id); showToast(t('settings.toastColorChanged')); }}
                          style={{ padding: '18px', borderRadius: '18px', cursor: 'pointer', border: `2px solid ${accent === opt.id ? opt.color : ibrd}`, background: accent === opt.id ? opt.color + '12' : ibg, display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.25s', fontFamily: 'inherit' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: opt.color, flexShrink: 0, boxShadow: `0 6px 20px ${opt.color}50` }} />
                          <div style={{ textAlign: 'left' }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: accent === opt.id ? opt.color : tp }}>{opt.label}</p>
                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: ts }}>{opt.color}</p>
                          </div>
                          {accent === opt.id && <span style={{ marginLeft: 'auto', color: opt.color, fontSize: '18px', fontWeight: 700 }}>✓</span>}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Tab 3: Xavfsizlik ── */}
            {tab === 3 && (
              <motion.div key="security" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiShield size={21} strokeWidth={1.8} /></div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>{t('settings.securityTitle')}</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>{t('settings.securityDescription')}</p>
                  </div>
                </div>
                <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* Current password */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: ts }}>{t('settings.labels.currentPassword')}</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showCur ? 'text' : 'password'} value={curPwd} onChange={e => setCurPwd(e.target.value)}
                        style={{ ...inp, paddingRight: '48px' }}
                        onFocus={e => e.target.style.borderColor = accentColor}
                        onBlur={e => e.target.style.borderColor = ibrd} />
                      <button type="button" onClick={() => setShowCur(!showCur)}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: ts, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
                        <EyeIcon visible={showCur} />
                      </button>
                    </div>
                  </div>

                  {/* New password + strength */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, color: ts }}>{t('settings.labels.newPassword')}</label>
                      {newPwd && <span style={{ fontSize: '12px', fontWeight: 700, color: sColor }}>{sLabel}</span>}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input type={showNew ? 'text' : 'password'} value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder={t('settings.pwdPlaceholder')}
                        style={{ ...inp, paddingRight: '48px' }}
                        onFocus={e => e.target.style.borderColor = accentColor}
                        onBlur={e => e.target.style.borderColor = ibrd} />
                      <button type="button" onClick={() => setShowNew(!showNew)}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: ts, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
                        <EyeIcon visible={showNew} />
                      </button>
                    </div>
                    {newPwd && (
                      <div style={{ display: 'flex', gap: '5px', marginTop: '4px' }}>
                        {[1,2,3,4,5].map(i => (
                          <motion.div key={i} animate={{ background: strength >= i ? sColor : ibrd }}
                            style={{ flex: 1, height: '4px', borderRadius: '4px', transition: 'background 0.3s' }} />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: ts }}>{t('settings.labels.confirmPassword')}</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showConf ? 'text' : 'password'} value={confPwd} onChange={e => setConfPwd(e.target.value)}
                        style={{ ...inp, paddingRight: '48px', borderColor: confPwd ? (newPwd === confPwd ? '#22c55e' : '#ef4444') : ibrd }}
                        onFocus={e => { e.target.style.borderColor = confPwd ? (newPwd === confPwd ? '#22c55e' : '#ef4444') : accentColor; }}
                        onBlur={e => { e.target.style.borderColor = confPwd ? (newPwd === confPwd ? '#22c55e' : '#ef4444') : ibrd; }} />
                      <button type="button" onClick={() => setShowConf(!showConf)}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: ts, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
                        <EyeIcon visible={showConf} />
                      </button>
                    </div>
                    <AnimatePresence>
                      {confPwd && (
                        <motion.p key={newPwd === confPwd ? 'ok' : 'err'} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: newPwd === confPwd ? '#22c55e' : '#ef4444' }}>
                          {newPwd === confPwd ? <FiCheckCircle size={14} /> : <FiAlertCircle size={14} />} {newPwd === confPwd ? t('settings.pwdMatch') : t('settings.pwdNoMatch')}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ padding: '14px 28px', borderRadius: '14px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, fontSize: '15px', cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit' }}>
                    {t('settings.buttons.passwordUpdate')}
                  </motion.button>
                </form>
              </motion.div>
            )}

            {/* ── Tab 5: Hisobot ── */}
            {tab === 5 && (
              <motion.div key="report" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiFileText size={21} strokeWidth={1.8} /></div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>Hisobot</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>Doska faoliyatini tanlangan sana bo‘yicha PDF formatda yuklab oling.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '18px' }}>
                  <div style={{ padding: '12px 14px', borderRadius: '14px', background: ibg, border: `1px solid ${ibrd}` }}>
                    <div style={{ fontSize: '11px', color: ts, fontWeight: 700, marginBottom: '8px' }}>Doskalar</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {boardOptions.map((board) => {
                        const isSelected = reportBoard === board.id;
                        return (
                          <button key={board.id} type="button" onClick={() => setReportBoard(board.id)}
                            style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${isSelected ? accentColor : ibrd}`, background: isSelected ? accentColor + '18' : 'transparent', color: isSelected ? accentColor : tp, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {board.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', borderRadius: '14px', background: ibg, border: `1px solid ${ibrd}` }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: ts, fontWeight: 700, marginBottom: '2px' }}>Sana oralig‘i</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatReportRange(reportStartDate, reportEndDate)}</div>
                    </div>
                    <button type="button" onClick={openReportCalendar}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '10px', border: `1px solid ${ibrd}`, background: accentColor + '16', color: accentColor, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                      <FiCalendar size={15} /> Sana tanlash
                    </button>
                  </div>

                  {showReportCalendar && (
                    <div style={{ position: 'relative', zIndex: 30 }}>
                      <div onClick={() => setShowReportCalendar(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.28)', backdropFilter: 'blur(2px)' }} />
                      <div style={{ position: 'absolute', right: 0, top: 8, width: 'min(320px, calc(100vw - 32px))', padding: '16px', borderRadius: '18px', background: isDark ? '#111827' : '#fff', border: `1px solid ${ibrd}`, boxShadow: '0 24px 64px rgba(15, 23, 42, 0.25)', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <button type="button" onClick={() => setReportCalendarMonth(new Date(reportCalendarMonth.getFullYear(), reportCalendarMonth.getMonth() - 1, 1))} style={{ ...buttonStyle, width: '32px', height: '32px', fontSize: '24px' }}>‹</button>
                          <div style={{ fontSize: '14px', fontWeight: 700 }}>{reportCalendarMonth.toLocaleDateString('uz-UZ', { month: 'long', year: 'numeric' })}</div>
                          <button type="button" onClick={() => setReportCalendarMonth(new Date(reportCalendarMonth.getFullYear(), reportCalendarMonth.getMonth() + 1, 1))} style={{ ...buttonStyle, width: '32px', height: '32px', fontSize: '24px' }}>›</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px', textAlign: 'center', fontSize: '11px', color: ts }}>
                          {['Ya','Du','Se','Ch','Pa','Ju','Sh'].map((day) => <div key={day}>{day}</div>)}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px', marginTop: '8px' }}>
                          {getCalendarGrid(reportCalendarMonth).map((date) => {
                            const isoDate = date.toISOString().slice(0, 10);
                            const isThisMonth = date.getMonth() === reportCalendarMonth.getMonth();
                            const isSelectedStart = isoDate === reportDraftStartDate;
                            const isSelectedEnd = isoDate === reportDraftEndDate;
                            const isInRange = reportDraftStartDate && reportDraftEndDate && isoDate >= reportDraftStartDate && isoDate <= reportDraftEndDate;
                            return (
                              <button key={isoDate} type="button" onClick={() => handleReportDayClick(date)}
                                style={{ height: '34px', borderRadius: '9px', border: 'none', background: isSelectedStart || isSelectedEnd ? accentColor : isInRange ? accentColor + '22' : isThisMonth ? (isDark ? '#1f2937' : '#f1f5f9') : 'transparent', color: isSelectedStart || isSelectedEnd ? '#fff' : tp, fontWeight: isSelectedStart || isSelectedEnd ? 700 : 500, opacity: isThisMonth ? 1 : 0.45, cursor: 'pointer', fontFamily: 'inherit' }}>
                                {date.getDate()}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '14px' }}>
                          <button type="button" onClick={cancelReportDateRange} style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: `1px solid ${ibrd}`, background: 'transparent', color: tp, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Bekor qilish</button>
                          <button type="button" onClick={confirmReportDateRange} style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Tasdiqlash</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))', gap: '12px', marginBottom: '18px' }}>
                  {[
                    { label: 'Jami', value: reportSummary.total, accent: accentColor },
                    { label: 'Deadlinegacha yopilgan', value: reportSummary.onTime, accent: '#22c55e' },
                    { label: "Deadline'dan keyin yopilgan", value: reportSummary.late, accent: '#ef4444' },
                  ].map((item) => (
                    <div key={item.label} style={{ padding: '14px 12px', borderRadius: '14px', background: ibg, border: `1.5px solid ${ibrd}`, minHeight: '92px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '11px', color: ts, fontWeight: 700, marginBottom: '8px' }}>{item.label}</div>
                      <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 800, color: item.accent, lineHeight: 1 }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '16px 18px', borderRadius: '16px', background: ibg, border: `1.5px solid ${ibrd}`, marginBottom: '18px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>Tanlangan ma’lumotlar</div>
                    <div style={{ color: ts, fontSize: '13px', marginTop: '2px' }}>{reportRows.length} ta kartochka</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={downloadBoardReportPdf}
                    disabled={!reportStartDate || !reportEndDate || !reportRows.length}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '12px', border: 'none', background: !reportStartDate || !reportEndDate || !reportRows.length ? '#94a3b8' : accentColor, color: '#fff', fontWeight: 700, cursor: !reportStartDate || !reportEndDate || !reportRows.length ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                    <FiDownload size={17} /> PDF yuklash
                  </motion.button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '430px', overflowY: 'auto', paddingRight: '6px' }}>
                  {reportRows.length ? reportRows.map((row, index) => (
                    <div key={row.id || `${row.title}-${index}`}
                      style={{ display: 'flex', gap: '12px', width: '100%', padding: '14px 16px', borderRadius: '14px', background: isDark ? '#111827' : '#f8fafc', border: `1.5px solid ${ibrd}`, textAlign: 'left', color: tp, fontFamily: 'inherit' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '15px', wordBreak: 'break-word' }}>{index + 1}. {row.title}</strong>
                            <span style={{ padding: '3px 8px', borderRadius: '999px', background: accentColor + '18', color: accentColor, fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{row.cardStatus}</span>
                          </div>
                          <div style={{ color: ts, lineHeight: 1.6, fontSize: '12px' }}>
                            <div><strong>Doska:</strong> {row.boardId}</div>
                            <div><strong>Yaratuvchi:</strong> {row.creator}</div>
                            <div><strong>Yaratilgan:</strong> {row.createdAt}</div>
                            <div><strong>Hozirgi status:</strong> {row.cardStatus}</div>
                            <div><strong>Status vaqti:</strong> {row.statusChangedAt}</div>
                            <div><strong>Statusni o‘zgartirgan:</strong> {row.statusChangedBy}</div>
                            <div><strong>Mas’ul:</strong> {row.assignee}</div>
                            <div><strong>Deadline:</strong> {row.deadline}</div>
                            <div><strong>Yangilangan:</strong> {row.updatedAt}</div>
                            <div><strong>Faoliyat:</strong> {row.activity}</div>
                            {row.statusHistory?.length ? (
                              <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${ibrd}` }}>
                                <strong>Status tarixi:</strong>
                                <div style={{ marginTop: '4px', display: 'grid', gap: '2px' }}>
                                  {row.statusHistory.map((entry, historyIndex) => (
                                    <div key={`${row.id}-history-${historyIndex}`}>
                                      {entry.status} — {entry.date} — {entry.author}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )) : (
                    <div style={{ padding: '28px', borderRadius: '16px', background: ibg, border: `1.5px dashed ${ibrd}`, textAlign: 'center', color: ts }}>
                      Tanlangan sana oralig‘ida kartochka topilmadi.
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Tab 4: Sayt haqida ── */}
            {tab === 4 && (
              <motion.div key="about" initial={{ opacity: 0, x: dir * 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 48 }} transition={tabSlide} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: accentColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>ℹ️</div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700 }}>Sayt haqida</h2>
                    <p style={{ margin: '4px 0 0', color: ts, fontSize: '14px' }}>Loyiha haqida umumiy ma'lumot va maqsadlar.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', maxHeight: '72vh', overflowY: 'auto', paddingRight: '8px', scrollbarWidth: 'thin', scrollbarColor: `${accentColor} transparent` }}>
                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h3 style={{ margin: '0 0 10px', fontWeight: 800, fontSize: '18px', color: accentColor }}>Platforma haqida</h3>
                    <div style={{ color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      <h4 style={{ margin: '18px 0 8px', fontSize: '16px', fontWeight: 800 }}>Bu platforma kim tomonidan yaratilgan?</h4>
                      <p style={{ margin: 0 }}>
                        Ushbu platforma <strong>Shaxram Razzoqov</strong> tomonidan yaratilgan.
                      </p>
                      <p style={{ margin: '12px 0 0' }}>
                        Shaxram Razzoqov — SMM sohasida faoliyat yurituvchi mutaxassis va ushbu platformaning g‘oyasi, konsepsiyasi hamda rivojlanish yo‘nalishini ishlab chiqqan loyiha muallifi.
                      </p>
                      <p style={{ margin: '12px 0 0' }}>
                        Platforma real ish jarayonlarida yuzaga keladigan muammolarni hal qilish va SMM hamda kreativ jamoalar faoliyatini maksimal darajada tizimlashtirish maqsadida ishlab chiqilgan.
                      </p>
                    </div>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>G‘oya qanday paydo bo‘lgan?</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Platforma g‘oyasi <strong>2025-yilda</strong> paydo bo‘lgan.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      SMM jarayonlarida vazifalarning turli chatlarda yo‘qolib ketishi, dizayner va montajyorlar bilan ishlashdagi kommunikatsiya muammolari, deadline'larni nazorat qilishning qiyinligi va umumiy ish jarayonining yetarlicha tizimlashmaganligi asosiy muammolardan biri edi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Bundan tashqari, jamoa ichidagi kommunikatsiya, vazifalarga bo‘lgan mas’uliyat, ish sifati va topshiriqlarning aniqligi bilan bog‘liq muammolar ham platformani yaratish zaruratini yuzaga keltirdi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Shu ehtiyojdan kelib chiqib, barcha jarayonlarni <strong>bitta markazlashgan tizimga birlashtirish</strong> g‘oyasi shakllandi.
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Platforma qanday yaratilgan?</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Loyiha ustida ishlash 2025-yilda boshlangan g‘oyadan keyin bosqichma-bosqich davom ettirildi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Platformaning <strong>birinchi ishlaydigan versiyasi 2026-yil 22-iyunda</strong> tayyorlandi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Ishlab chiqish jarayonida zamonaviy dasturlash vositalari va texnologiyalaridan, jumladan <strong>Visual Studio Code va EmailJS</strong> xizmatidan foydalanildi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Loyihaning ishlab chiqilishida <strong>sun’iy intellekt texnologiyalari ham yordamchi vosita sifatida faol qo‘llanildi</strong>.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Xususan, <strong>ChatGPT, Claude, Gemini va GitHub</strong> kabi vositalardan kod yozish, texnik yechimlarni ishlab chiqish, xatolarni aniqlash va tuzatish, shuningdek interfeys va dizayn yechimlarini takomillashtirishda foydalanildi.
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Platformaning asosiy maqsadi</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Platformaning asosiy maqsadi — <strong>SMM va kreativ jamoalarning ishini maksimal darajada tizimlashtirish, tartibga solish va boshqarishni soddalashtirish</strong>.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Bu shunchaki vazifalar ro‘yxati emas. Platforma orqali ish jarayonini boshidan oxirigacha nazorat qilish, jamoa a’zolari o‘rtasidagi hamkorlikni yaxshilash va har bir vazifaning holatini aniq ko‘rish imkoniyati yaratiladi.
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Kimlar uchun yaratilgan?</h4>
                    <ul style={{ margin: 0, paddingLeft: '18px', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      <li>SMM menejerlar</li>
                      <li>SMM va marketing agentliklari</li>
                      <li>Freelancerlar</li>
                      <li>Dizaynerlar</li>
                      <li>Video montajyorlar</li>
                      <li>Kompaniyalar va ichki kreativ jamoalar</li>
                    </ul>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Platformaning imkoniyatlari</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Platformadagi asosiy funksiyalar real vaqt rejimida ishlaydi va ish jarayonlarini yagona tizim orqali boshqarishga imkon beradi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Vazifalar, loyihalar, jamoa faoliyati, muddatlar va boshqa ish jarayonlarini bir joyda nazorat qilish mumkin.
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Platformani boshqalardan ajratib turadigan jihat</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Ushbu platformaning asosiy g‘oyasi — foydalanuvchiga <strong>erkinlik va nazoratni bir vaqtning o‘zida berish</strong>.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Ish tizimlashtirilgani sari vazifalar tartibga tushadi, vaqt samaraliroq taqsimlanadi va inson faqat ish jarayonini kuzatish bilan cheklanib qolmay, o‘z vaqtini boshqa muhim ishlar va shaxsiy hayotiga ham ajrata oladi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      <strong>Maqsad — ko‘proq ishlash emas, balki ishni to‘g‘ri tashkil qilish.</strong>
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Kelajakdagi rivojlanish</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Platforma yakunlangan mahsulot sifatida emas, balki doimiy rivojlanib boradigan tizim sifatida ko‘riladi.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Kelajakda platformaga Telegram bot, avtomatik eslatmalar, AI yordamchi, kengaytirilgan analitika, mijozlar uchun alohida kabinet va avtomatik hisobotlar kabi imkoniyatlarni qo‘shish rejalashtirilgan.
                    </p>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: ibg, border: `1.5px solid ${ibrd}` }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 800, color: accentColor }}>Asosiy g‘oya</h4>
                    <p style={{ margin: 0, color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      <strong>Ishni o‘zingizga moslashtiring. Jarayonlarni tizimlashtiring. Vaqtingizni qadrlang.</strong>
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Ushbu platforma bir muammodan kelib chiqqan oddiy g‘oya sifatida boshlangan va real ish jarayonlari asosida shakllangan tizimga aylangan.
                    </p>
                    <p style={{ margin: '12px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      Uning asosiy maqsadi — insonni ishga bog‘lab qo‘yish emas, <strong>ish jarayonini shunday tashkil qilishki, inson o‘z ishini nazorat qilgan holda o‘z vaqtini ham boshqara olsin.</strong>
                    </p>
                    <p style={{ margin: '20px 0 0', color: tp, lineHeight: 1.9, fontSize: '15px' }}>
                      <strong>Loyiha muallifi</strong><br />
                      Shaxram Razzoqov<br />
                      <span style={{ opacity: 0.8 }}>SMM mutaxassisi, loyiha g‘oyasi va konsepsiyasi muallifi</span>
                    </p>
                  </div>

                </div>

                <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-start' }}>
                  <motion.button
                    type="button"
                    onClick={downloadSiteInfoPdf}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '14px 22px',
                      borderRadius: '14px',
                      border: 'none',
                      background: accentColor,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '15px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      boxShadow: `0 12px 24px ${accentColor}35`
                    }}
                  >
                    PDF yuklab olish
                  </motion.button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
